<template>
  <SectionWithAnchor :anchorId="isFromLLP ? AnchorLinks.PropertyValuation : undefined">
    <div
      :class="[
        $style.container,
        {
          [$style.is24]: isIs24,
          [$style.hg]: !isIs24,
        },
        { [$style.llp]: isFromLLP },
      ]"
    >
      <div :class="$style.background"></div>
      <div :class="$style.contentWrapper">
        <HgBadge :theme="isIs24 ? 'pink' : 'greenlight'" :class="$style.badge">
          <HgIcon
            :class="$style.trendIcon"
            icon="TrendingUp"
            :theme="isIs24 ? 'success' : 'current'"
            size="small"
          />
          {{ $t('landing.herobox.subtitle') }}
        </HgBadge>
        <h2 :class="$style.heading">
          {{ $t('landing.cta2.title') }}&nbsp;<span v-if="!isIs24" :class="$style.highlight">{{
            $t('landing.cta2.duration')
          }}</span>
        </h2>
        <p :class="$style.description">{{ $t('landing.cta2.text') }}</p>
        <HgButton
          :class="$style.ctaButton"
          :theme="isFromLLP ? 'secondary' : 'primary'"
          @click="onClickCtaButton"
        >
          {{ $t('landing.cta2.link') }}
        </HgButton>
        <div :class="$style.checkmarksContainer">
          <div :class="$style.checkmarkItem">
            <HgIcon icon="Check" size="x-small" :class="$style.checkIcon" />
            <span>{{ $t('landing.cta2.checkmark1') }}</span>
          </div>
          <div :class="$style.checkmarkItem">
            <HgIcon icon="Check" size="x-small" :class="$style.checkIcon" />
            <span>{{ $t('landing.cta2.checkmark2') }}</span>
          </div>
        </div>
      </div>
      <div :class="$style.imageWrapper">
        <div :class="$style.gradientEffectContainer">
          <div :class="$style.gradientHeartImage" aria-hidden="true"></div>
        </div>
        <img
          :class="$style.priceChart"
          :src="chartImageUrl"
          loading="lazy"
          :alt="$t('landing.img.pricechart.alt')"
        />
      </div>
    </div>
  </SectionWithAnchor>
</template>

<script setup lang="ts">
import { HgBadge, HgButton, HgIcon } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import landingPriceChartHg from '@/assets/images/landing-valuation/landing-price-chart-hg.svg?url';
import landingPriceChartIs24 from '@/assets/images/landing-valuation/landing-price-chart-is24.svg?url';
import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { getCurrentPlatform, isPlatformIs24 } from '@/helpers/Platform/Platform';
import { normalizeRouteQuery } from '@/helpers/Route/normalizeRouteQuery';
import { VALUATION_FUNNEL_PATH } from '@/router/routes/constants';
import { useCommonStore } from '@/stores/common/common';
import { trackingGenericClickEvent, trackingLandingPageButtonEvent } from '@/tracking/tracking';
import {
  LANDING_PAGE_BUTTON_ACTION,
  LANDING_PAGE_BUTTON_EVENT,
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { AnchorLinks, Pages } from '@/types';

// Composables
const router = useRouter();
const route = useRoute();
const commonStore = useCommonStore();
const { t, locale } = useI18n();

// Reactive state
const isIs24 = isPlatformIs24();
const platform = getCurrentPlatform();

// Computed properties
const currentPage = computed(() => commonStore.currentPage);
const isFromLLP = computed(() => currentPage.value === Pages.LLP);
const chartImageUrl = computed(() =>
  platform === 'is24' ? landingPriceChartIs24 : landingPriceChartHg,
);

// Methods
const onClickCtaButton = () => {
  if (currentPage.value === Pages.LLP) {
    trackingGenericClickEvent({
      event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
      category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
      action: LOCAL_LANDING_PAGE_ACTION.VALUATION_FUNNEL,
      label: 'bottom_banner_cta',
    });
  } else if (currentPage.value === Pages.LP) {
    trackingLandingPageButtonEvent(
      LANDING_PAGE_BUTTON_ACTION.SELECT_PROPERTY_TYPE,
      LANDING_PAGE_BUTTON_EVENT.CLICK,
    );
  }
  goToPropertyValuation();
};

const goToPropertyValuation = () => {
  router.push({
    path: `${VALUATION_FUNNEL_PATH[locale.value]}`,
    ...(Object.keys(route.query).length && { query: normalizeRouteQuery(route.query) }),
  });
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.container {
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  border-radius: $border-radius-l;

  @include grid-media-query(sm) {
    flex-direction: row;
    gap: $space-500;
  }

  @include grid-media-query(md) {
    gap: $space-800;
  }
}

.contentWrapper {
  flex: 1;
  padding: $space-250 $space-100;

  @include grid-media-query(sm) {
    padding: $space-250;
  }
}

.imageWrapper {
  position: relative;
  height: 344px;

  @include background-image;

  background-image: url('@/assets/images/landing-valuation/landing-cta2-background-hg.jpg');

  @include grid-media-query(sm) {
    flex: 0 0 245px;
    width: 245px;
    height: auto;
    border-top-right-radius: $border-radius-l;
    border-bottom-right-radius: $border-radius-l;
  }

  @include grid-media-query(md) {
    flex: 0 0 400px;
    width: 400px;
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  margin-bottom: $space-50;
}

.trendIcon {
  margin-right: $space-50;
}

.heading {
  @include vertical-margin(0, $space-50);
  @include heading-2;

  .highlight {
    display: inline-block;

    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 4px;
      background: url('@/assets/images/hg-brand-underline.svg?url') no-repeat center / contain;
      background-size: 100% auto;
    }
  }
}

.description {
  @include vertical-margin(0, $space-100);
  @include body-1($color-grey-120);
}

.checkmarksContainer {
  display: flex;
  flex-wrap: wrap;
  gap: $space-75;
  margin-top: $space-75;
  height: $space-100 + $space-25;
  align-items: center;
  @include body-3;
}

.checkmarkItem {
  display: flex;
  align-items: center;
  gap: $space-25;
}

.checkIcon {
  flex-shrink: 0;
}

.priceChart {
  position: absolute;
  bottom: -$space-100;
  left: 50%;
  width: 286px;
  height: 208px;
  border-radius: $border-radius-l;
  box-shadow: $elevation-medium;
  transform: translateX(-50%);

  @include grid-media-query(sm) {
    bottom: -$space-250;
    left: -$space-300;
    width: 259px;
    height: 188px;
    transform: translateX(0);
  }

  @include grid-media-query(md) {
    bottom: -$space-300;
    left: -$space-600;
    width: 330px;
    height: 240px;
  }
}

.container.hg {
  position: relative;

  .background {
    background-color: $color-blue-0;
    position: absolute;
    inset: 0;
    z-index: -2;
  }

  .imageWrapper {
    position: relative;
    background-image: url('@/assets/images/landing-valuation/landing-cta2-background-hg.jpg');

    .gradientEffectContainer {
      position: absolute;
      inset: 0;
      left: -40%;
      overflow: clip;

      .gradientHeartImage {
        position: absolute;
        inset: 0;
        z-index: -1;
        width: 25rem;
        height: 25rem;
        pointer-events: none;
        background: url('@/assets/images/effects/gradient-circle.webp') no-repeat center / contain;
        transform: rotate(50deg);
        opacity: 0.7;
      }
    }
  }
}

.container.is24 {
  background-color: $color-cyan-60;

  .background {
    display: none;
  }

  .imageWrapper {
    background-image: url('@/assets/images/landing-valuation/landing-cta2-background-is24.jpg');
  }

  .badge {
    color: $color-cyan-130;
    background-color: $color-teal-60;
    border-color: $color-teal-60;

    path {
      fill: $color-cyan-130;
    }
  }
}

.container.llp {
  flex-direction: column-reverse;

  &.is24 {
    background-color: $color-grey-30;
  }

  @include grid-media-query(sm) {
    flex-direction: row;
  }

  .ctaButton {
    width: 100%;

    @include grid-media-query(sm) {
      width: fit-content;
    }
  }
}
</style>
